import { getStudentFromStorage } from "@helpers/migrateLogin";
import { WAIT_DELAY } from "@modules/shared/components/RetryGuard";
import { captureException as captureExceptionMain } from "@sentry/nextjs";
import { useEffect } from "react";

export const captureException = (...data) => {
    console.log("Multiplayer exception", data);

    captureExceptionMain(data[0], data[1] || {});
};

export const useMultiplayerLoadingExceptions = ({
    round,
    content,
    currentRound,
    isFetching,
    isScaled,
    allComponentsLoaded,
    componentsLoaded,
    roundRef,
    getComponentIndex,
    isQuestionPreview = false,
}) => {
    useEffect(() => {
        if (isQuestionPreview) return;
        let noRoundTimeOut;
        if (!round) {
            noRoundTimeOut = setTimeout(() => {
                captureException(
                    new Error(
                        `Round is undefined for round ${currentRound} of block ${content.id}`,
                    ),
                    {
                        error: "Round is undefined",
                        errorInfo: {
                            round_number: currentRound,
                            block_id: content?.id || content?.tmpId,
                            // data_of_block: (content?.data),
                            block_number: content?.data?.blockIndex,
                            user: getStudentFromStorage(),
                        },
                        href: window.location.href,
                    },
                );
            }, WAIT_DELAY);
        }

        if (round && noRoundTimeOut) {
            clearTimeout(noRoundTimeOut);
            // @ts-ignore
            noRoundTimeOut = null;
        }

        let roundNotLoadedTimeout;
        if (round && !round.is_loaded) {
            roundNotLoadedTimeout = setTimeout(() => {
                captureException(new Error("Round is not loaded"), {
                    error: "Round is not loaded",
                    errorInfo: {
                        round_number: currentRound,
                        round,
                        block_id: content?.id || content?.tmpId,
                        // data_of_block: (content?.data),
                        block_number: content?.data?.blockIndex,
                        user: getStudentFromStorage(),
                    },
                    href: window.location.href,
                });
            }, WAIT_DELAY);
        }

        return () => {
            if (noRoundTimeOut) {
                clearTimeout(noRoundTimeOut);
                // @ts-ignore
                noRoundTimeOut = null;
            }

            if (roundNotLoadedTimeout) {
                clearTimeout(roundNotLoadedTimeout);
                // @ts-ignore
                roundNotLoadedTimeout = null;
            }
        };
    }, [round, isQuestionPreview]);

    useEffect(() => {
        let timeout;
        if (isFetching) {
            timeout = setTimeout(() => {
                captureException(
                    new Error(
                        `Fetching is taking too long for round ${currentRound} of block ${content?.id}`,
                    ),
                    {
                        error: "Fetching is taking too long",
                        errorInfo: {
                            round_number: currentRound,
                            round: roundRef.current,
                            block_id: content?.id || content?.tmpId,
                            // data_of_block: (content?.data),
                            block_number: content?.data?.blockIndex,
                            user: getStudentFromStorage(),
                        },
                        href: window.location.href,
                    },
                );
            }, WAIT_DELAY);
        }

        return () => {
            if (timeout) {
                clearTimeout(timeout);
                // @ts-ignore
                timeout = null;
            }
        };
    }, [isFetching]);

    useEffect(() => {
        let timeout;
        if (!isScaled) {
            timeout = setTimeout(() => {
                captureException(
                    new Error(
                        `Scaling is taking too long for round ${currentRound} of block ${content?.id}`,
                    ),
                    {
                        error: "Scaling is taking too long",
                        errorInfo: {
                            round_number: currentRound,
                            round: roundRef.current,
                            block_id: content?.id || content?.tmpId,
                            // data_of_block: (content?.data),
                            block_number: content?.data?.blockIndex,
                            user: getStudentFromStorage(),
                        },
                        href: window.location.href,
                    },
                );
            }, WAIT_DELAY);
        }

        return () => {
            if (timeout) {
                clearTimeout(timeout);
                // @ts-ignore
                timeout = null;
            }
        };
    }, [isScaled]);

    useEffect(() => {
        if (isQuestionPreview) return;
        let timeout;
        if (!allComponentsLoaded) {
            timeout = setTimeout(() => {
                const components_loaded = {
                    player_1: roundRef?.current?.player_1?.components.map(
                        (c, i) => {
                            return {
                                type: c.type,
                                index: i,
                                loaded: componentsLoaded[
                                    getComponentIndex("player_1", i)
                                ],
                            };
                        },
                    ),
                    common_arena:
                        roundRef?.current?.common_arena?.components.map(
                            (c, i) => {
                                return {
                                    type: c.type,
                                    index: i,
                                    loaded: componentsLoaded[
                                        getComponentIndex("common_arena", i)
                                    ],
                                };
                            },
                        ),
                    player_2: roundRef?.current?.player_2?.components.map(
                        (c, i) => {
                            return {
                                type: c.type,
                                index: i,
                                loaded: componentsLoaded[
                                    getComponentIndex("player_2", i)
                                ],
                            };
                        },
                    ),
                };

                console.log(
                    "components_loaded",
                    JSON.stringify(components_loaded),
                );

                captureException(
                    new Error(
                        `Loading components is taking too long for round ${currentRound} of block ${content?.id}`,
                    ),
                    {
                        error: `Loading components is taking too long for round ${currentRound} of block ${content?.id}`,
                        errorInfo: {
                            round_number: currentRound,
                            round: roundRef.current,
                            block_id: content?.id || content?.tmpId,
                            // data_of_block: (content?.data),
                            block_number: content?.data?.blockIndex,
                            user: getStudentFromStorage(),
                            components_loaded,
                        },
                        href: window.location.href,
                    },
                );
            }, WAIT_DELAY);
        }

        return () => {
            if (timeout) {
                clearTimeout(timeout);
                // @ts-ignore
                timeout = null;
            }
        };
    }, [allComponentsLoaded, componentsLoaded, isQuestionPreview]);
};

export const withTryCatch = (fn) => {
    return (...args) => {
        try {
            return fn(...args);
        } catch (error) {
            captureException(error);
        }
    };
};
